import { useAppDispatch } from 'app/hooks';
import { useForm, ValidationErrorType } from 'hooks/useForm';
import { useRole } from 'hooks/useRole';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditPromotionMutation, useGetPromotionQuery } from '../services';
import { useGetPlacesQuery } from 'features/place/services';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useWorkspace } from 'hooks/useWorkspace';
import { useTranslation } from 'react-i18next';
import { useGetProductsWithJoinQuery } from 'features/product/services';
import { PROMOTION_FIELDS_SCHEMA, PROMOTION_INITIAL_FIELDS } from '../constants/promotion-fields';
import { AllErrorsType, ProductConfigData } from './add-promotion-controller';
import { Promotion, PromotionDto } from '../types/dtos';
import { validateConfigFields } from 'utils/products-config-validation-helper';
import { PROMOTION_TYPE } from '../constants/type';
import { SelectChangeEvent } from '@mui/material';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { removeEmpty, trimValues } from 'utils/object-helpers';

const useController = () => {
    const { promotionId, placeId = '' } = useParams();
    const [productConfigData, setProductConfigData] = useState<ProductConfigData[]>([]);
    const [placeIds, setPlaceIds] = useState<string[]>([]);
    const [priceErrors, setPriceErrors] = useState<Record<string, ValidationErrorType>>({});
    const { isAdmin } = useRole();
    const { websiteId } = useWorkspace();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [editPromotion, { isLoading: isEditPromotionLoading }] = useEditPromotionMutation();
    const { data: promotion } = useGetPromotionQuery(promotionId as string);

    const { data: places, isLoading: isGetPlacesLoading } = useGetPlacesQuery({
        ...FETCH_ALL_PARAMS,
        ids: placeId,
    });
    const { data: products, isLoading: isGetProductsLoading } = useGetProductsWithJoinQuery({
        ...FETCH_ALL_PARAMS,
        placeId: placeId,
    });
    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } = useForm({
        validationSchema: PROMOTION_FIELDS_SCHEMA,
        options: {
            trackChangedValues: true,
        },
    });

    const initialProductConfigsRef = useRef<ProductConfigData[]>([]);

    const isSelectedProductChanged = useMemo(() => {
        return (
            productConfigData.length !== products?.content.length ||
            productConfigData.some((config) => !products?.content.some((product) => product.id === config.productId))
        );
    }, [productConfigData, products?.content]);

    useEffect(() => {
        if (promotion) {
            const newProductConfigData = promotion.products?.map((product) => {
                return {
                    productId: product.id as string,
                    discountedPrice: product.discountedPrice === null ? '' : product.discountedPrice?.toString(),
                    name: product.name,
                    price: product.price,
                };
            });

            if (newProductConfigData) {
                setProductConfigData(newProductConfigData);
                if (initialProductConfigsRef.current.length === 0) {
                    initialProductConfigsRef.current = [...newProductConfigData];
                }
            }

            setPlaceIds([placeId]);

            const newPromotion = { ...promotion };
            delete newPromotion.products;
            setInitialFormData({
                ...PROMOTION_INITIAL_FIELDS,
                ...newPromotion,
            });
        }
    }, [promotion, setInitialFormData, placeId]);

    const hasProductConfigsChanged = useMemo(() => {
        if (initialProductConfigsRef.current.length !== productConfigData.length) {
            return true;
        }
        return !initialProductConfigsRef.current.every((initialConfig, index) => {
            const currentConfig = productConfigData[index];

            return initialConfig.discountedPrice === currentConfig?.discountedPrice;
        });
    }, [productConfigData]);

    const validatePrices = useCallback(
        (configs: ProductConfigData[]): Record<string, ValidationErrorType> => {
            const isDiscountPromotion = formData?.type === PROMOTION_TYPE.DISCOUNTED;

            return validateConfigFields<ProductConfigData>(configs, 'productId', ['discountedPrice'], {
                discountedPrice: {
                    required: isDiscountPromotion
                        ? {
                              message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_REQUIRED'),
                          }
                        : undefined,
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_POSITIVE'),
                    },
                    custom: isDiscountPromotion
                        ? [
                              {
                                  validator: (value, allValues) => {
                                      if (!value || !allValues.price) return true;
                                      const discounted = parseFloat(value);
                                      const price = parseFloat(allValues.price);
                                      return discounted < price;
                                  },
                                  message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_LOWER_THAN_PRICE'),
                              },
                          ]
                        : undefined,
                },
            });
        },
        [formData?.type, t],
    );

    const handlePlaceIdsSelectChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const {
                target: { value },
            } = event;
            const selectedIds = typeof value === 'string' ? value.split(',') : value;

            if (places?.content) {
                const newPlaceIds = places.content
                    .filter((place) => selectedIds.includes(place.id))
                    .map((place) => {
                        const existing = placeIds.find((id) => id === place.id);
                        return existing || place.id;
                    });

                setPlaceIds(newPlaceIds);
            }
        },
        [places?.content, placeIds, setPlaceIds],
    );

    const handleProductSelectChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const {
                target: { value },
            } = event;
            const selectedIds = typeof value === 'string' ? value.split(',') : value;

            if (selectedIds.length === 0) {
                setProductConfigData([]);
                return;
            }

            if (products?.content) {
                const removedProductIds = productConfigData
                    .map((config) => config.productId)
                    .filter((id) => !selectedIds.includes(id));

                const newProductConfigData = products.content
                    .filter((product) => selectedIds.includes(product.id))
                    .map((product) => ({
                        productId: product.id,
                        name: product.name,
                        price: product.price,
                        discountedPrice: product.discountedPrice?.split(' ')[0],
                    }));

                const newErrors = validatePrices(newProductConfigData);

                setPriceErrors((prevErrors) => {
                    const cleanedErrors = { ...prevErrors };
                    removedProductIds.forEach((id) => {
                        Object.keys(cleanedErrors)
                            .filter((key) => key.includes(`-${id}`))
                            .forEach((errorKey) => {
                                delete cleanedErrors[errorKey];
                            });
                    });
                    return {
                        ...cleanedErrors,
                        ...newErrors,
                    };
                });

                setProductConfigData(newProductConfigData);
            }
        },
        [products?.content, productConfigData, setProductConfigData, setPriceErrors, validatePrices],
    );

    const allErrors: AllErrorsType<typeof PROMOTION_INITIAL_FIELDS> = useMemo(() => {
        return {
            ...errors,
            ...priceErrors,
        };
    }, [errors, priceErrors]);

    const handleProductConfigPriceChange = useCallback(
        (field: string, value: string, errorPrefixes: string[], productId?: string) => {
            const updatedData = productId
                ? productConfigData.map((config) =>
                      config.productId === productId ? { ...config, [field]: value } : config,
                  )
                : productConfigData.map((config) => ({ ...config, [field]: value }));

            const newErrors = validatePrices(updatedData);

            setPriceErrors((errors) => ({
                ...Object.fromEntries(
                    Object.entries(errors).filter(([key]) => !errorPrefixes.some((prefix) => key.startsWith(prefix))),
                ),
                ...newErrors,
            }));

            setProductConfigData(updatedData);
        },
        [productConfigData, setProductConfigData, setPriceErrors, validatePrices],
    );

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'promotion.PROMOTION_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );
    const getPayload = useCallback(() => {
        const data = { ...changedValues };

        if (data.importance) data.importance = Number(data.importance);

        if (!isSelectedProductChanged || hasProductConfigsChanged) {
            data.products = productConfigData.map((config) => {
                return {
                    productId: config.productId,
                    ...(config.discountedPrice !== undefined &&
                        promotion?.type === PROMOTION_TYPE.DISCOUNTED && {
                            discountedPrice: Number(config.discountedPrice),
                        }),
                };
            });
        }

        return trimValues(removeEmpty(data)) as PromotionDto;
    }, [productConfigData, changedValues, isSelectedProductChanged, hasProductConfigsChanged, promotion]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();

                    await editPromotion({ id: promotionId as string, payload }).unwrap();

                    handleSuccess();
                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [getPayload, handleErrors, editPromotion, handleSubmit, handleSuccess, navigate, promotionId],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData: formData as Promotion,
        places,
        products,
        onChange: handleChange,
        onSubmit,
        onCancel,
        dataChanged: isDirty || !isSelectedProductChanged || hasProductConfigsChanged,
        isLoading: formData === undefined || isGetPlacesLoading || isGetProductsLoading,
        isEditPromotionLoading: isEditPromotionLoading,
        errors: allErrors,
        isAdmin,
        websiteId,
        placeId,
        placeIds,
        handlePlaceIdsSelectChange,
        handleProductSelectChange,
        productConfigData,
        handleProductConfigPriceChange,
    };
};

export default useController;
